import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { FilterWithSwapOption } from '../../../../common/components/DataTable/DataTableFilters';
import { IMainTable } from '../../../../common/models/common';
import configDataRetentionService from '../../../../services/master/dataRetention/dataRetention.service';
import DataTable from '../../../../common/components/DataTable';
import { Can } from '../../../../common/ability';
import { Action, Page } from '../../../../common/constants/pageAction';
import { Common } from '../../../../common/constants/common';
import { showDateFromApi } from '../../../../common/helperFunction';
import {
  clearConfigDataRetentionMessages,
  configDataRetentionSelector,
  setTableColumnSelection,
} from '../../../../store/master/dataRetention/dataRetention.reducer';
import { searchConfigDataRetention } from '../../../../store/master/dataRetention/dataRetention.action';
import { IConfigDataRetention } from '../../../../services/master/dataRetention/dataRetention.model';

const MAINTENANCE_RULE = 'Maintenance';

// Cutoff the procedure would use if it ran today (UTC). AgeKeepLatestSnapshot also keeps the newest
// snapshot older than this date, so its actual cutoff can be earlier (see the Data Retention Log).
export const getCutoffPreview = (row: IConfigDataRetention): string => {
  if (!row || row.rule_type === MAINTENANCE_RULE || !row.retention_months) {
    return '';
  }
  return moment
    .utc()
    .startOf('day')
    .subtract(row.retention_months, 'months')
    .format(Common.DATEFORMAT);
};

const MainTable: React.ForwardRefRenderFunction<unknown, IMainTable> = (props, ref) => {
  const { setSelectedId } = props;
  const dataTableRef = useRef(null);
  const history = useHistory();
  const [ObjectForColumnFilter, setObjectForColumnFilter] = useState({});

  useImperativeHandle(ref, () => ({
    refreshData() {
      dataTableRef?.current.refreshData();
    },
  }));

  // Dropdown (distinct values) for category columns, text for free text and dates. Values come
  // from this page's own /column-lookup endpoint: these tables are deliberately not registered
  // with the generic column-lookup / import / bulk-update endpoints.
  const DROPDOWN_COLUMNS = [
    'sort_order',
    'step_name',
    'table_name',
    'rule_type',
    'date_column',
    'retention_months',
    'keep_latest_dataset',
    'exempt_company_ids',
    'is_enabled',
    'batch_size',
  ];
  const getColumnLookup = (filter: any) => configDataRetentionService.getColumnLookup(filter);
  const FilterText = (dataIndex: string, form) => {
    const isDropdown = DROPDOWN_COLUMNS.includes(dataIndex);
    return FilterWithSwapOption(
      dataIndex,
      '',
      form,
      isDropdown ? getColumnLookup : null,
      ObjectForColumnFilter,
      false,
      false,
      false,
      !isDropdown
    );
  };

  const column = (title: string, dataIndex: string, form, extra: { [key: string]: any } = {}) => ({
    title: <span className="dragHandler">{title}</span>,
    column: dataIndex,
    sorter: true,
    ellipsis: true,
    children: [
      {
        title: FilterText(dataIndex, form),
        dataIndex,
        key: dataIndex,
        ellipsis: true,
        ...extra,
      },
    ],
  });

  const getTableColumns = (form) => {
    return [
      column('Order', 'sort_order', form),
      column('Step', 'step_name', form),
      column('Table', 'table_name', form),
      column('Rule', 'rule_type', form),
      column('Date Column', 'date_column', form),
      column('Retention Months', 'retention_months', form),
      {
        title: <span className="dragHandler">Deletes Older Than (if run today)</span>,
        column: 'cutoff_preview',
        ellipsis: true,
        children: [
          {
            title: '',
            dataIndex: 'cutoff_preview',
            key: 'cutoff_preview',
            ellipsis: true,
            render: (_value, record: IConfigDataRetention) => getCutoffPreview(record),
          },
        ],
      },
      column('Keep Latest Dataset', 'keep_latest_dataset', form, {
        render: (value: boolean, record: IConfigDataRetention) =>
          record?.rule_type !== 'Age' ? 'n/a' : value ? 'Yes' : 'No',
      }),
      column('Exempt Company Ids', 'exempt_company_ids', form),
      column('Enabled', 'is_enabled', form, {
        render: (value: boolean) => (!_.isNil(value) ? (value ? 'Yes' : 'No') : ''),
      }),
      column('Batch Size', 'batch_size', form, {
        render: (value: number) => (!_.isNil(value) ? value : 'Default (4000)'),
      }),
      column('Notes', 'notes', form),
      column('Date Modified', 'date_modified', form, {
        render: (date: Date) => (!_.isNil(date) ? showDateFromApi(date, true) : ''),
      }),
    ];
  };

  const tableAction = (_value, data: any) => (
    <div className="btns-block">
      <Can I={Action.Update} a={Page.ConfigDataRetention}>
        <a
          className="action-btn"
          onClick={() => {
            setSelectedId(data.id);
            history.push(`/administration/config-data-retention/${data.id}`);
          }}
        >
          <img src={`${process.env.PUBLIC_URL}/assets/images/ic-edit.svg`} alt="" />
        </a>
      </Can>
    </div>
  );

  return (
    <>
      <DataTable
        ref={dataTableRef}
        showAddButton={false}
        showDelete={false}
        showBulkUpdate={false}
        hideExportButton={true}
        disableRowSelection={true}
        setSelectedId={setSelectedId}
        tableAction={tableAction}
        getTableColumns={getTableColumns}
        reduxSelector={configDataRetentionSelector}
        searchTableData={searchConfigDataRetention}
        clearTableDataMessages={clearConfigDataRetentionMessages}
        setTableColumnSelection={setTableColumnSelection}
        setObjectForColumnFilter={setObjectForColumnFilter}
        globalSearchExist={false}
      />
    </>
  );
};

export default forwardRef(MainTable);
